import { Controller } from "@hotwired/stimulus"

export default class extends Controller {
  connect() {
    this._boundHideTooltips = this._hideTooltips.bind(this)
    document.addEventListener('htmx:beforeSwap', this._boundHideTooltips)

    this._onTinyMCEAddEditor = ({ editor }) => {
      editor.on('input change', () => {
        const el = editor.getElement()
        if (el instanceof HTMLTextAreaElement) el.value = editor.getContent()
      })
    }
    if (this._tinyMCEAvailable()) {
      tinyMCE.get().forEach(editor => this._onTinyMCEAddEditor({ editor }))
      tinyMCE.on('AddEditor', this._onTinyMCEAddEditor)
    }

    this._saveTinyMCEBeforeSubmit = () => {
      if (this._tinyMCEAvailable()) tinyMCE.triggerSave()
    }
    this._destroyTinyMCEInTarget = (event) => {
      const target = event.detail?.target
      if (!target || !this._tinyMCEAvailable()) return
      tinyMCE.get().forEach(editor => {
        if (target.contains(editor.getElement())) editor.destroy()
      })
    }
    this._reinitTinyMCE = () => {
      if (!this._tinyMCEReady()) return
      initTinyMCEBasicToolbar('.tinymce-basic-toolbar')
      this._initShortToolbars(document)
    }
    // TinyMCE iframes break when initialized inside a hidden container; init on reveal instead.
    this._allowTinyMCEFocus = (event) => {
      if (event.target.closest('.tox-tinymce-aux, .tox-dialog, .tox-dialog-wrap'))
        event.stopImmediatePropagation()
    }
    this._onBsModalShown = (event) => {
      if (!this._tinyMCEReady()) return
      this._initModalEditors(event.target)
    }
    this._onBsModalHide = (event) => {
      if (!this._tinyMCEAvailable()) return
      this._destroyModalEditors(event.target)
    }

    this._onCommentRequest = (event) => {
      const form = event.detail?.elt
      if (!form?.matches?.('form') || !form.querySelector('#ticket-comment-body')) return

      if (this._commentIsBlank(form)) {
        event.preventDefault()
        this._showCommentError(form, 'Please enter a comment before sending.')
        return
      }

      this._pendingCommentScroll = true
    }

    this._clearPendingCommentScroll = (event) => {
      const form = event.detail?.elt
      if (!form?.matches?.('form') || !form.querySelector('#ticket-comment-body')) return
      if (!event.detail?.successful) this._pendingCommentScroll = false
    }

    this._scrollCommentsToLatest = () => {
      if (!this._pendingCommentScroll) return
      this._pendingCommentScroll = false

      const comments = this.element.querySelector('.ticket-comments')
      if (comments) comments.scrollTop = comments.scrollHeight
    }

    document.addEventListener('submit', this._saveTinyMCEBeforeSubmit, true)
    this.element.addEventListener('htmx:beforeRequest', this._onCommentRequest)
    this.element.addEventListener('htmx:afterRequest', this._clearPendingCommentScroll)
    this.element.addEventListener('htmx:beforeSwap', this._destroyTinyMCEInTarget)
    this.element.addEventListener('htmx:afterSettle', this._reinitTinyMCE)
    this.element.addEventListener('htmx:afterSettle', this._scrollCommentsToLatest)
    document.addEventListener('focusin', this._allowTinyMCEFocus, true)
    document.addEventListener('shown.bs.modal', this._onBsModalShown)
    document.addEventListener('hide.bs.modal', this._onBsModalHide)
    this._reinitTinyMCE()
  }

  disconnect() {
    document.removeEventListener('htmx:beforeSwap', this._boundHideTooltips)
    if (this._tinyMCEAvailable() && this._onTinyMCEAddEditor)
      tinyMCE.off('AddEditor', this._onTinyMCEAddEditor)
    document.removeEventListener('submit', this._saveTinyMCEBeforeSubmit, true)
    this.element.removeEventListener('htmx:beforeRequest', this._onCommentRequest)
    this.element.removeEventListener('htmx:afterRequest', this._clearPendingCommentScroll)
    this.element.removeEventListener('htmx:beforeSwap', this._destroyTinyMCEInTarget)
    this.element.removeEventListener('htmx:afterSettle', this._reinitTinyMCE)
    this.element.removeEventListener('htmx:afterSettle', this._scrollCommentsToLatest)
    document.removeEventListener('focusin', this._allowTinyMCEFocus, true)
    document.removeEventListener('shown.bs.modal', this._onBsModalShown)
    document.removeEventListener('hide.bs.modal', this._onBsModalHide)
  }

  _hideTooltips() {
    document.querySelectorAll('.tooltip').forEach(el => el.remove())
  }

  toggleEdit(event) {
    event.preventDefault()
    const toggle = event.currentTarget
    const show = document.getElementById(toggle.dataset.target)
    const edit = document.getElementById(toggle.dataset.target2)
    if (show) show.classList.toggle('d-none')
    if (!edit) return

    const wasHidden = edit.classList.contains('d-none')
    edit.classList.toggle('d-none')

    if (wasHidden) {
      this._initShortToolbars(edit)
    } else if (this._tinyMCEAvailable()) {
      edit.querySelectorAll('.tinymce-basic-toolbar-short').forEach(el => {
        tinyMCE.get(el.id)?.destroy()
      })
    }
  }

  copyLink(event) {
    event.preventDefault()
    const btn = event.currentTarget
    navigator.clipboard.writeText(btn.dataset.url).then(() => {
      const check = this._iconNode('check')
      const current = btn.querySelector('svg')
      if (!check || !current) return

      this._copyLinkState ||= new WeakMap()
      const previous = this._copyLinkState.get(btn)
      // Only the first click's icon is the real one; a repeat click would
      // otherwise capture the check and leave it stuck there.
      const original = previous?.original || current
      clearTimeout(previous?.timer)

      check.classList.add('text-success')
      current.replaceWith(check)
      this._copyLinkState.set(btn, {
        original,
        timer: setTimeout(() => check.replaceWith(original), 1500)
      })
    })
  }

  showFiles(event) {
    this._renderFileDisplay(event.target)
  }

  initSelect2(event) {
    this._initSelect2OnTarget(event.target)
  }

  // firstElementChild, not firstChild: HAML leaves a whitespace text node
  // ahead of the <svg> inside the template.
  _iconNode(name) {
    const template = this.element.querySelector(`template[data-ticket-icon="${name}"]`)
    return template?.content.firstElementChild?.cloneNode(true) || null
  }

  _commentIsBlank(form) {
    const textarea = form.querySelector('#ticket-comment-body')
    if (!textarea) return false

    const editorContent = this._tinyMCEAvailable() ? tinyMCE.get(textarea.id)?.getContent() : null
    const probe = document.createElement('div')
    probe.innerHTML = editorContent ?? textarea.value ?? ''

    if (probe.querySelector('img, table, iframe')) return false

    return probe.textContent.replace(/\u00a0/g, ' ').trim() === ''
  }

  _showCommentError(form, message) {
    form.querySelectorAll('.ticket-comment-error').forEach(el => el.remove())

    const alert = document.createElement('div')
    alert.className = 'alert alert-danger ticket-comment-error'
    alert.setAttribute('role', 'alert')
    alert.textContent = message
    form.insertAdjacentElement('afterbegin', alert)

    setTimeout(() => alert.remove(), 5000)
  }

  _tinyMCEAvailable() {
    return typeof tinyMCE !== 'undefined'
  }

  _tinyMCEReady() {
    return typeof initTinyMCEBasicToolbar !== 'undefined' && typeof tinyMCE !== 'undefined'
  }

  _initModalEditors(container) {
    container.querySelectorAll('.tinymce-basic-toolbar').forEach(el => {
      if (el.id && !tinyMCE.get(el.id)) initTinyMCEBasicToolbar('#' + el.id)
    })
  }

  _destroyModalEditors(container) {
    container.querySelectorAll('.tinymce-basic-toolbar').forEach(el => {
      if (el.id) tinyMCE.get(el.id)?.destroy()
    })
  }

  _initShortToolbars(scope) {
    if (!this._tinyMCEReady()) return
    scope.querySelectorAll('.tinymce-basic-toolbar-short').forEach(el => {
      if (el.id && !el.closest('.d-none') && !tinyMCE.get(el.id))
        initTinyMCEBasicToolbar(`#${el.id}`, 150)
    })
  }

  _renderFileDisplay(input) {
    const fileDisplay = document.getElementById('selected-files')
    if (!fileDisplay) return

    fileDisplay.innerHTML = ''
    const files = Array.from(input.files || [])

    if (files.length === 0) {
      fileDisplay.textContent = 'No files selected'
      return
    }

    files.forEach((file, index) => {
      const icon = this._iconNode('paperclip')

      const span = document.createElement('span')
      span.textContent = ' ' + file.name
      if (icon) span.prepend(icon)

      const removeBtn = document.createElement('button')
      removeBtn.type = 'button' // sits inside the comment form; must not submit it
      removeBtn.classList.add('bg-transparent', 'border-0', 'p-0', 'lh-1', 'align-baseline', 'cursor-pointer', 'text-danger', 'ms-2')
      removeBtn.setAttribute('aria-label', `Remove ${file.name}`)
      const removeIcon = this._iconNode('x')
      if (removeIcon) removeBtn.appendChild(removeIcon)
      removeBtn.addEventListener('click', (event) => {
        event.preventDefault()
        const updated = Array.from(input.files).filter((_, i) => i !== index)
        const dt = new DataTransfer()
        updated.forEach(f => dt.items.add(f))
        input.files = dt.files
        this._renderFileDisplay(input)
      })

      const fileItem = document.createElement('div')
      fileItem.appendChild(span)
      fileItem.appendChild(removeBtn)
      fileDisplay.appendChild(fileItem)
    })
  }

  _initSelect2OnTarget(target) {
    if (typeof $ === 'undefined' || !$.fn.select2) return
    const modal = target.closest('.modal')
    const dropdownParent = modal ? $(modal) : $('body')
    target.querySelectorAll('.basic-select2').forEach(el => {
      if (!el.classList.contains('select2-hidden-accessible'))
        $(el).select2({ dropdownParent })
    })
  }
}
